// PORT-001 — contrôle des sorties au portail.
// Fonctions pures (sauf fermerSession et genererRequeteId, isolées en fin de fichier) :
// fusion des verdicts be_caisse avec la liste be_clinique, recherche, libellés, confinement du rôle.
// Règle générale : échec fermé. Dans le doute, un patient n'est jamais affiché comme « À jour ».

export const ROLE_AGENT_SECURITE = 'ROLE_AGENT_SECURITE'
export const ROLE_ADMINISTRATEUR = 'ROLE_ADMINISTRATEUR'
export const ROUTE_PORTAIL_SORTIES = '/portail/sorties'
// Rôles qui peuvent ouvrir l'écran (même liste que la capacité be_config).
export const ROLES_PORTAIL = [ROLE_AGENT_SECURITE, ROLE_ADMINISTRATEUR]
// Rôles acceptés par l'interface Caisse (liste blanche de login.js et du routeur).
export const ROLES_INTERFACE_CAISSE = ['ROLE_COMPTABLE', ROLE_ADMINISTRATEUR, 'ROLE_CAISSIERE', 'ROLE_TRESORERIE', 'ROLE_ACCUEIL', ROLE_AGENT_SECURITE]
export const ROUTE_GUIDE = '/guide-utilisation'
export const ROUTE_GUIDE_PORTAIL = ROUTE_GUIDE + '?article=controle-sorties'
// Chemins accessibles à l'agent : son écran, son profil, le guide (lecture seule), la connexion
// et l'écran de licence suspendue (évite une boucle de redirection).
export const ROUTES_AGENT = [ROUTE_PORTAIL_SORTIES, '/parametres/profil', ROUTE_GUIDE, '/login', '/blocked']

export const DUREE_SESSION_AGENT_MS = 12 * 60 * 60 * 1000
export const INTERVALLE_ACTUALISATION_MS = 60 * 1000
export const DELAI_DONNEES_PERIMEES_MS = 5 * 60 * 1000
export const MAX_PATIENTS = 500
// Blocs de recherche (rouge, bleu, orange) : requête d'au moins 3 caractères ou 2 chiffres, 5 noms au plus par bloc.
export const LONGUEUR_MIN_RECHERCHE = 3
export const MAX_NOMS_BLOC = 5
// Une horloge qui recule de plus de 5 min depuis la connexion invalide la session (échec fermé).
const TOLERANCE_HORLOGE_MS = 5 * 60 * 1000

const NATURES = { consultation: 'Consultation', hospitalisation: 'Hospitalisation', observation: 'Observation' }
const TONS_NATURE = { consultation: 'primary', hospitalisation: 'warning', observation: 'muted' }
const LIBELLES_ROLES = { ROLE_ADMINISTRATEUR: 'Administrateur', ROLE_COMPTABLE: 'Comptable', ROLE_CAISSIERE: 'Caissière',
    ROLE_TRESORERIE: 'Trésorerie', ROLE_ACCUEIL: 'Accueil' }
const HEURE = /^([01]\d|2[0-3]):[0-5]\d$/
// Jour de libération au-delà d'hier (fenêtre be_clinique réglée au-delà de 24 h) : « JJ/MM/AAAA ».
const DATE_JOUR = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/
// Début d'un instant ISO 8601 du serveur : l'heure « HH:mm » est lue telle quelle, sans conversion.
const ISO_HEURE = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):([0-5]\d)/

function texte(valeur) {
    return valeur === null || valeur === undefined ? '' : String(valeur)
}

function entierPositif(valeur) {
    if (valeur === null || valeur === undefined || valeur === '' || typeof valeur === 'boolean') return 0
    const nombre = Number(valeur)
    return Number.isInteger(nombre) && nombre > 0 ? nombre : 0
}

export function heureValide(valeur) {
    return typeof valeur === 'string' && HEURE.test(valeur)
}

// ---------------------------------------------------------------------------------------------
// Rôle et session
// ---------------------------------------------------------------------------------------------

function rolesUtilisateur(auth_info) {
    const user = auth_info && auth_info.user
    return user && Array.isArray(user.roles) ? user.roles.filter(role => role && typeof role.nom === 'string') : []
}

/**
 * Vrai si l'interface doit être confinée au portail.
 * Rôle actif ROLE_AGENT_SECURITE, ou, faute de rôle actif Caisse (rôle nul ou d'une autre application),
 * un utilisateur dont le seul rôle Caisse est ROLE_AGENT_SECURITE (échec fermé pendant le changement de rôle).
 */
export function estAgentSecurite(auth_info, active_role) {
    if (!auth_info) return false
    const nom_actif = active_role && typeof active_role.nom === 'string' ? active_role.nom : ''
    if (nom_actif === ROLE_AGENT_SECURITE) return true
    if (nom_actif && ROLES_INTERFACE_CAISSE.indexOf(nom_actif) !== -1) return false
    const roles_caisse = rolesUtilisateur(auth_info).filter(role => ROLES_INTERFACE_CAISSE.indexOf(role.nom) !== -1)
    return roles_caisse.length > 0 && roles_caisse.every(role => role.nom === ROLE_AGENT_SECURITE)
}

export function peutOuvrirPortail(active_role) {
    return !!active_role && ROLES_PORTAIL.indexOf(active_role.nom) !== -1
}

export function routeAutoriseeAgent(chemin) {
    const propre = texte(chemin).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'
    return ROUTES_AGENT.indexOf(propre) !== -1
}

/**
 * Autres rôles de l'interface Caisse attribués à l'utilisateur (hors agent de sécurité), dédoublonnés.
 * Ils permettent de quitter le rôle agent depuis la coque du portail, qui n'a pas de menu de rôles.
 */
export function autresRolesCaisse(auth_info) {
    const vus = Object.create(null)
    return rolesUtilisateur(auth_info).filter(role => {
        if (role.nom === ROLE_AGENT_SECURITE || ROLES_INTERFACE_CAISSE.indexOf(role.nom) === -1) return false
        const role_id = entierPositif(role.id) || entierPositif(role.pivot && role.pivot.role_id)
        if (!role_id || vus[role_id]) return false
        vus[role_id] = true
        return true
    }).map(role => ({
        id: entierPositif(role.id) || entierPositif(role.pivot && role.pivot.role_id),
        nom: role.nom,
        libelle: texte(role.description).trim() || LIBELLES_ROLES[role.nom] || role.nom
    }))
}

// Espaces réellement servis par les interfaces Clinicaa. Ne pas proposer un rôle inconnu
// dont la destination serait ambiguë sur un poste de contrôle des sorties.
const ROLES_CLINIQUE = ['ROLE_INFIRMIER', 'ROLE_MEDECIN', 'ROLE_TECH_SUP_LABO', 'ROLE_MEDECIN_BIOLOGISTE',
    'ROLE_MEDECIN_TRAVAIL', 'ROLE_SAGE_FEMME', 'ROLE_INFIRMIER_MEDECINE_TRAVAIL', 'ROLE_TECHNICIEN_IMAGERIE', 'ROLE_RADIOLOGUE']
const ROLES_STOCK = ['ROLE_PHARMACIEN', 'ROLE_PHARMACIEN_INTERMITENT', 'ROLE_RESPONSABLE_LOGISTIQUE',
    'ROLE_RECEPTIONNISTE', 'ROLE_RECEPTIONNISTE_INTERMITENT']

export function espaceRole(nom_role) {
    if (ROLES_INTERFACE_CAISSE.indexOf(nom_role) !== -1) return 'caisse'
    if (ROLES_CLINIQUE.indexOf(nom_role) !== -1) return 'clinique'
    if (ROLES_STOCK.indexOf(nom_role) !== -1) return 'stock'
    if (nom_role === 'ROLE_RH') return 'rh'
    return ''
}

export function autresRolesPortail(auth_info) {
    const vus = Object.create(null)
    return rolesUtilisateur(auth_info).filter(role => {
        const id = entierPositif(role.id) || entierPositif(role.pivot && role.pivot.role_id)
        if (!id || role.nom === ROLE_AGENT_SECURITE || !espaceRole(role.nom) || vus[id]) return false
        vus[id] = true
        return true
    }).map(role => ({
        id: entierPositif(role.id) || entierPositif(role.pivot && role.pivot.role_id),
        nom: role.nom,
        libelle: texte(role.description).trim() || LIBELLES_ROLES[role.nom] || role.nom
    }))
}

/** Page d'arrivée après un changement de rôle (mêmes destinations que le menu de rôles de la navbar). */
export function routeApresChangementRole(nom_role) {
    if (nom_role === ROLE_ADMINISTRATEUR || nom_role === 'ROLE_COMPTABLE') return '/tableau-de-bord'
    if (nom_role === 'ROLE_TRESORERIE') return '/tresorerie'
    return '/'
}

/** Session d'agent expirée 12 h après la connexion (horodatage posé par l'action login). */
export function sessionAgentExpiree(auth_info, maintenant) {
    const debut = auth_info ? Number(auth_info.logged_at) : 0
    if (!debut || !Number.isFinite(debut)) return true
    const ecart = Number(maintenant) - debut
    if (!Number.isFinite(ecart)) return true
    return ecart >= DUREE_SESSION_AGENT_MS || ecart < -TOLERANCE_HORLOGE_MS
}

/**
 * Décision du routeur pour PORT-001. Renvoie null (navigation normale) ou { chemin, fermer_session }.
 * - agent : session de plus de 12 h → /login et fermeture ; toute autre route que le portail → portail ;
 * - autre rôle : l'écran du portail n'est ouvert qu'à l'administrateur.
 */
export function redirectionPortail(chemin, auth_info, active_role, maintenant) {
    if (!auth_info) return null
    if (estAgentSecurite(auth_info, active_role)) {
        if (sessionAgentExpiree(auth_info, maintenant)) return { chemin: '/login', fermer_session: true }
        if (!routeAutoriseeAgent(chemin)) return { chemin: ROUTE_PORTAIL_SORTIES, fermer_session: false }
        return null
    }
    const propre = texte(chemin).split('?')[0].replace(/\/+$/, '')
    if (propre === ROUTE_PORTAIL_SORTIES && !peutOuvrirPortail(active_role)) return { chemin: '/', fermer_session: false }
    return null
}

// ---------------------------------------------------------------------------------------------
// Réponses serveur
// ---------------------------------------------------------------------------------------------

/** Capacité be_config : { success: true, data: { access_token, expires_in, expires_at, actor_id } }. */
export function lireJeton(reponse) {
    const data = reponse && reponse.success === true ? reponse.data : null
    if (!data || typeof data.access_token !== 'string' || !data.access_token) return null
    const auth_id = entierPositif(data.actor_id)
    if (!auth_id) return null
    return { access_token: data.access_token, auth_id }
}

/** Liste blanche des clés affichables d'un candidat be_clinique ; null si l'identifiant est invalide. */
export function nettoyerCandidat(candidat) {
    if (!candidat || typeof candidat !== 'object') return null
    const patient_id = entierPositif(candidat.patient_id)
    if (!patient_id) return null
    const age = candidat.age === null || candidat.age === undefined || candidat.age === '' ? null : Number(candidat.age)
    return {
        patient_id,
        nom_affiche: texte(candidat.nom_affiche).trim(),
        sexe: texte(candidat.sexe).trim(),
        age: Number.isInteger(age) && age >= 0 ? age : null,
        dossier: texte(candidat.dossier).trim(),
        nature: texte(candidat.nature).trim(),
        libere_jour: texte(candidat.libere_jour),
        libere_heure: texte(candidat.libere_heure)
    }
}

export function nettoyerSorti(sorti) {
    if (!sorti || typeof sorti !== 'object') return null
    const patient_id = entierPositif(sorti.patient_id)
    if (!patient_id) return null
    return {
        patient_id,
        nom_affiche: texte(sorti.nom_affiche).trim(),
        dossier: texte(sorti.dossier).trim(),
        sorti_heure: texte(sorti.sorti_heure),
        constate_par_moi: sorti.constate_par_moi === true || sorti.constate_par_moi === 1 || sorti.constate_par_moi === '1'
    }
}

function dedoublonner(liste, nettoyer) {
    const vus = Object.create(null)
    const resultat = []
    ;(Array.isArray(liste) ? liste : []).forEach(item => {
        const propre = nettoyer(item)
        if (!propre || vus[propre.patient_id]) return
        vus[propre.patient_id] = true
        resultat.push(propre)
    })
    return resultat
}

export function nettoyerCandidats(liste) { return dedoublonner(liste, nettoyerCandidat) }
export function nettoyerSortis(liste) { return dedoublonner(liste, nettoyerSorti) }

/** Identifiants envoyés à be_caisse : entiers ≥ 1, dédoublonnés, 500 au plus. */
export function idsCandidats(candidats) {
    return nettoyerCandidats(candidats).map(candidat => candidat.patient_id).slice(0, MAX_PATIENTS)
}

/** Réponse be_caisse { success: true, verdicts: [{ patient_id, a_jour }] } ; null si inexploitable. */
export function lireVerdicts(reponse) {
    return reponse && reponse.success === true && Array.isArray(reponse.verdicts) ? reponse.verdicts : null
}

/**
 * Fusionne la liste des libérés et les verdicts de paiement.
 * Seul a_jour === true (booléen strict) donne « À laisser sortir ». Verdict absent, non booléen,
 * contradictoire, ou verdicts indisponibles (null) : « indéterminé », jamais en vert.
 * Renvoie { a_sortir, a_regulariser, indetermines }.
 */
export function fusionnerVerdicts(candidats, verdicts) {
    const resultat = { a_sortir: [], a_regulariser: [], indetermines: [] }
    let index = null
    if (Array.isArray(verdicts)) {
        index = Object.create(null)
        verdicts.forEach(verdict => {
            const patient_id = verdict && typeof verdict === 'object' ? entierPositif(verdict.patient_id) : 0
            if (!patient_id) return
            const valeur = typeof verdict.a_jour === 'boolean' ? verdict.a_jour : 'invalide'
            index[patient_id] = patient_id in index && index[patient_id] !== valeur ? 'conflit' : valeur
        })
    }
    nettoyerCandidats(candidats).forEach(candidat => {
        const verdict = index ? index[candidat.patient_id] : undefined
        if (verdict === true) resultat.a_sortir.push(candidat)
        else if (verdict === false) resultat.a_regulariser.push(candidat)
        else resultat.indetermines.push(candidat)
    })
    return resultat
}

// ---------------------------------------------------------------------------------------------
// Recherche
// ---------------------------------------------------------------------------------------------

/** Minuscules sans accents, ponctuation remplacée par des espaces, espaces compactés. */
export function normaliserRecherche(valeur) {
    return texte(valeur)
        .replace(/œ/g, 'oe').replace(/Œ/g, 'OE').replace(/æ/g, 'ae').replace(/Æ/g, 'AE').replace(/ß/g, 'ss')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
}

function cleRecherche(patient) {
    const nom = normaliserRecherche(patient && patient.nom_affiche)
    const dossier = normaliserRecherche(patient && patient.dossier)
    const inverse = nom.split(' ').reverse().join(' ')
    return {
        texte: [nom, inverse, dossier].join(' | '),
        compact: [nom.replace(/ /g, ''), inverse.replace(/ /g, ''), dossier.replace(/ /g, '')].join('|')
    }
}

/** Chaque mot saisi (dans n'importe quel ordre NOM / Prénom) doit figurer dans le nom ou le dossier. */
export function correspond(patient, recherche) {
    const requete = normaliserRecherche(recherche)
    if (!requete) return true
    const cle = cleRecherche(patient)
    if (requete.split(' ').every(mot => cle.texte.indexOf(mot) !== -1)) return true
    return cle.compact.indexOf(requete.replace(/ /g, '')) !== -1
}

export function filtrer(liste, recherche) {
    const source = Array.isArray(liste) ? liste : []
    if (!normaliserRecherche(recherche)) return source
    return source.filter(patient => correspond(patient, recherche))
}

/**
 * Requête assez précise pour les blocs de recherche : au moins 3 caractères (espaces exclus)
 * ou 2 chiffres consécutifs d'un numéro de dossier. En deçà, les listes se filtrent sans bloc.
 */
export function rechercheSuffisante(recherche) {
    const requete = normaliserRecherche(recherche)
    return requete.replace(/ /g, '').length >= LONGUEUR_MIN_RECHERCHE || /\d{2}/.test(requete)
}

/**
 * État de la recherche pour les blocs vert / rouge / orange.
 * saisie : un texte est tapé ; active : il est assez précis pour afficher les blocs ;
 * absent : recherche active et aucun patient trouvé, ni à sortir, ni à régulariser, ni déjà sorti.
 */
export function etatRecherche(recherche, fusion, sortis) {
    const groupes = fusion || { a_sortir: [], a_regulariser: [], indetermines: [] }
    const saisie = !!normaliserRecherche(recherche)
    const active = saisie && rechercheSuffisante(recherche)
    const resultat = {
        saisie,
        active,
        incomplete: saisie && !active,
        a_sortir: filtrer(groupes.a_sortir, recherche),
        a_regulariser: active ? filtrer(groupes.a_regulariser, recherche) : [],
        indetermines: active ? filtrer(groupes.indetermines, recherche) : [],
        sortis: filtrer(sortis, recherche)
    }
    resultat.absent = active && !resultat.a_sortir.length && !resultat.a_regulariser.length &&
        !resultat.indetermines.length && !resultat.sortis.length
    return resultat
}

/** Aperçu d'un bloc de recherche : 5 patients au plus, puis « … et N autres : précisez la recherche ». */
export function apercuBloc(liste) {
    const source = Array.isArray(liste) ? liste : []
    const reste = Math.max(0, source.length - MAX_NOMS_BLOC)
    return {
        patients: source.slice(0, MAX_NOMS_BLOC),
        reste,
        libelle_reste: reste ? '… et ' + reste + (reste > 1 ? ' autres' : ' autre') + ' : précisez la recherche' : ''
    }
}

export function libelleNombrePatients(nombre) {
    const n = Number.isInteger(nombre) && nombre > 0 ? nombre : 0
    return n + (n > 1 ? ' patients' : ' patient')
}

export function compterOnglets(fusion, sortis) {
    return {
        a_sortir: fusion && Array.isArray(fusion.a_sortir) ? fusion.a_sortir.length : 0,
        sortis: Array.isArray(sortis) ? sortis.length : 0
    }
}

// ---------------------------------------------------------------------------------------------
// Libellés (aucune conversion de date par le navigateur : jour et heure viennent du serveur)
// ---------------------------------------------------------------------------------------------

/** Jour du serveur : « aujourdhui », « hier » ou « JJ/MM/AAAA » (fenêtre de plus de 24 h) ; toute autre valeur est ignorée. */
export function libelleJourLiberation(libere_jour) {
    if (libere_jour === 'aujourdhui') return 'aujourd’hui'
    if (libere_jour === 'hier') return 'hier'
    return typeof libere_jour === 'string' && DATE_JOUR.test(libere_jour) ? 'le ' + libere_jour : ''
}

export function libelleLiberation(candidat) {
    const jour = candidat ? libelleJourLiberation(candidat.libere_jour) : ''
    const heure = candidat && heureValide(candidat.libere_heure) ? 'à ' + candidat.libere_heure : ''
    return ['Libéré', jour, heure].filter(Boolean).join(' ')
}

export function libelleSexeAge(candidat) {
    const sexe = normaliserRecherche(candidat && candidat.sexe).charAt(0)
    const lettre = sexe === 'f' ? 'F' : sexe === 'm' || sexe === 'h' ? 'M' : ''
    const age = candidat && Number.isInteger(candidat.age) && candidat.age >= 0 ? candidat.age + (candidat.age > 1 ? ' ans' : ' an') : ''
    return [lettre, age].filter(Boolean).join(' · ')
}

export function libelleNature(nature) {
    return NATURES[nature] || 'Libération'
}

export function tonNature(nature) {
    return TONS_NATURE[nature] || 'muted'
}

export function libelleSortie(sorti) {
    const heure = sorti && heureValide(sorti.sorti_heure) ? ' à ' + sorti.sorti_heure : ''
    return 'Sorti' + heure + (sorti && sorti.constate_par_moi ? ' par vous' : ' par un autre agent')
}

/**
 * Heure « HH:mm » de genere_at (ISO 8601 dans le fuseau de la clinique, calculé par be_clinique),
 * lue dans la chaîne sans conversion : l'horloge des tablettes du portail est souvent fausse. '' si invalide.
 */
export function heureServeur(genere_at) {
    const correspondance = typeof genere_at === 'string' ? ISO_HEURE.exec(genere_at) : null
    return correspondance ? correspondance[1] + ':' + correspondance[2] : ''
}

/** Heure locale « HH:mm » d'un instant mesuré par le poste (repli de « Mis à jour à » sans genere_at). */
export function heureLocale(instant_ms) {
    const date = new Date(Number(instant_ms))
    if (Number.isNaN(date.getTime())) return ''
    const deux = nombre => (nombre < 10 ? '0' : '') + nombre
    return deux(date.getHours()) + ':' + deux(date.getMinutes())
}

export function donneesPerimees(derniere_maj, maintenant) {
    if (!derniere_maj) return true
    const ecart = Number(maintenant) - Number(derniere_maj)
    return !Number.isFinite(ecart) || ecart > DELAI_DONNEES_PERIMEES_MS || ecart < -TOLERANCE_HORLOGE_MS
}

/** Empreinte stable d'une réponse : pas de nouveau rendu si le JSON est inchangé. */
export function empreinte(valeur) {
    return JSON.stringify(valeur)
}

// ---------------------------------------------------------------------------------------------
// Messages (textes propres au portail : les messages techniques des capacités ne sont pas affichés)
// ---------------------------------------------------------------------------------------------

export function statutErreur(erreur) {
    return erreur && erreur.response ? Number(erreur.response.status) || 0 : 0
}

export function messageErreurLecture(statut, etape) {
    if (etape === 'acces' && statut === 401) return 'Votre session ne peut plus être authentifiée. Reconnectez-vous.'
    if (etape === 'acces' && statut === 403) return 'Votre compte ne dispose pas de l’accès au contrôle des sorties. Contactez un administrateur.'
    if (statut === 401) return 'Autorisation expirée. Actualisez la liste.'
    if (statut === 403) return 'Accès refusé par le service. Actualisez ou contactez un administrateur.'
    return 'Impossible de charger la liste des sorties. Vérifiez la connexion puis actualisez.'
}

export function messageConflit(erreur) {
    const data = erreur && erreur.response && erreur.response.data
    const message = data && typeof data.message === 'string' ? data.message.trim() : ''
    return message && message.length <= 200 ? message : 'Sortie impossible : le patient n’est plus dans la liste à laisser sortir.'
}

// ---------------------------------------------------------------------------------------------
// Effets de bord isolés
// ---------------------------------------------------------------------------------------------

/** Identifiant de requête (UUID v4) : le même identifiant est renvoyé après un résultat incertain. */
export function genererRequeteId() {
    const octets = []
    const crypto_navigateur = typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function' ? window.crypto : null
    if (crypto_navigateur) {
        const tampon = new Uint8Array(16)
        crypto_navigateur.getRandomValues(tampon)
        for (let i = 0; i < 16; i++) octets.push(tampon[i])
    } else {
        for (let i = 0; i < 16; i++) octets.push(Math.floor(Math.random() * 256))
    }
    octets[6] = (octets[6] & 0x0f) | 0x40
    octets[8] = (octets[8] & 0x3f) | 0x80
    const hex = octets.map(octet => (octet < 16 ? '0' : '') + octet.toString(16)).join('')
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20)
}

/** Ferme la session du poste : état Vuex d'authentification et copie persistée. */
export function fermerSession(store, stockage, tout_effacer) {
    if (store && typeof store.commit === 'function') {
        store.commit('SET_CREDENTIALS', null)
        store.commit('SET_ACTIVE_ROLE', null)
        store.commit('SET_AUTH_USER', [])
    }
    try {
        if (stockage) {
            stockage.removeItem('vuex')
            if (tout_effacer && typeof stockage.clear === 'function') stockage.clear()
        }
    } catch (erreur) {
        // Stockage indisponible (navigation privée) : l'état en mémoire est déjà vidé.
    }
}
